import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
    ClaimQueryControllerService,
    ClaimSearchRequest,
    PagedClaimSearchRequest,
    PagedResponseClaimResponse,
    PayerSummaryResponse,
    PayorBalanceResponse,
    SortablePagedRequestClaimSortBy,
} from '@core/generated/openapi';
import {
    ClaimSearchService,
    SnoozeFilterOption,
} from '@services/claim-search/claim-search.service';
import {
    DEFAULT_CLAIM_PAGING,
    STATUS_SEARCH_OPTIONS,
} from '@core/constants/claim-search.constants';
import { PageEvent } from '@angular/material/paginator';
import { TableService } from 'rcp-common-lib';
import { GridLoading } from 'rcp-common-lib';
import { AuthService } from '@services/auth/auth.service';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';
import { STALLED_CLAIM_FILTER_IDS } from '@core/constants/stalled-claim-filter.constants';

@Injectable({
    providedIn: 'root',
})
export class ClaimStatusSearchService {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );

    PAGED_CLAIMS: BehaviorSubject<PagedResponseClaimResponse> =
        new BehaviorSubject<PagedResponseClaimResponse>(null);

    paging: SortablePagedRequestClaimSortBy = JSON.parse(
        JSON.stringify(DEFAULT_CLAIM_PAGING),
    );

    claimStatusFilter: BehaviorSubject<any> = new BehaviorSubject<any>(
        STATUS_SEARCH_OPTIONS[0],
    );

    selectedSnooze: BehaviorSubject<SnoozeFilterOption> =
        new BehaviorSubject<SnoozeFilterOption>(SnoozeFilterOption.NonSnoozed);

    selectedPayer: BehaviorSubject<PayorBalanceResponse> =
        new BehaviorSubject<PayorBalanceResponse>(undefined);

    previousSelectedPayerId: number;

    snoozedClaimCount: BehaviorSubject<number> = new BehaviorSubject(0);

    isLoading: GridLoading = new BehaviorSubject<boolean>(true);

    setPageNumber(pageNumber: number) {
        this.paging.pageNumber = pageNumber;
    }

    getClaimStatusFilterStatusOption(): any {
        return this.claimStatusFilter.value;
    }

    setClaimStatusFilterStatusOption(statusOption: any) {
        this.claimStatusFilter.next(statusOption);
    }

    getSelectedPayerValue() {
        return this.selectedPayer.value;
    }

    setSelectedPayerValue(payer: PayorBalanceResponse) {
        this.selectedPayer.next(payer);
    }

    getSelectedSnoozeValue(): SnoozeFilterOption {
        return this.selectedSnooze.value;
    }

    setSelectedSnoozeValue(snoozeOption: SnoozeFilterOption) {
        this.selectedSnooze.next(snoozeOption);
    }

    getSnoozedClaimCount() {
        return this.snoozedClaimCount.value;
    }

    payerSummary: BehaviorSubject<PayerSummaryResponse> = new BehaviorSubject(
        undefined,
    );

    constructor(
        private searchService: ClaimSearchService,
        private queryService: ClaimQueryControllerService,
        private tableService: TableService,
        private authService: AuthService,
    ) {
        this.authService.isUserLoggedIn.subscribe((isLoggedIn) => {
            if (!isLoggedIn) {
                this.payerSummary.next(undefined);
                this.selectedPayer.next(undefined);
            }
        });

        this.claimStatusFilter.subscribe((filter) => {
            if (
                this.getSelectedSnoozeValue() !== SnoozeFilterOption.NonSnoozed
            ) {
                this.setSelectedSnoozeValue(SnoozeFilterOption.NonSnoozed);
            }
        });

        this.selectedPayer.subscribe((payer) => {
            if (
                this.getSelectedSnoozeValue() !== SnoozeFilterOption.NonSnoozed
            ) {
                this.setSelectedSnoozeValue(SnoozeFilterOption.NonSnoozed);
            }
        });

        this.snoozedClaimCount.subscribe((snoozeCount) => {
            if (
                this.getSelectedSnoozeValue() !==
                    SnoozeFilterOption.NonSnoozed &&
                snoozeCount === 0
            ) {
                this.setSelectedSnoozeValue(SnoozeFilterOption.NonSnoozed);
                this.refreshSearchData();
            }
        });
    }

    refreshSearchData(pageNumber = 1) {
        this.searchPayorBalances();
        if (this.selectedPayer && this.selectedPayer.value) {
            this.setPageNumber(pageNumber);
            this.getPagedClaimsBySearch();
        } else {
            this.PAGED_CLAIMS.next(<PagedResponseClaimResponse>[]);
        }
    }

    searchPayorBalances() {
        let claimSearchRequest: ClaimSearchRequest = {
            statusList: this.getStatusForSearch(),
            notSnoozed:
                this.searchService.getSnoozedSearchOptionFromSelectedValue(
                    SnoozeFilterOption.All,
                ),
            uploadAccountLocationIdIdList:
                this.authService.isAgingUploadByLocationOff(
                    this.authService.getAgingLocationFilter(),
                )
                    ? undefined
                    : this.authService.getAgingLocationFilter(),
            stalledReasonIsNull: this.getStalledStatusForSearch(),
        };

        this.queryService.findPayerSummary(claimSearchRequest).subscribe(
            (payers) => {
                this.payerSummary.next(payers);
                console.log('payers summary  object status search: ', payers);
            },
            (error) => {
                console.error('ERROR Looking up payers', error);
            },
        );
    }

    getStatusForSearch(): ClaimSearchRequest.StatusListEnum[] {
        let statuses: ClaimSearchRequest.StatusListEnum[] = [];

        switch (this.claimStatusFilter.value) {
            case STATUS_SEARCH_OPTIONS[0]:
            case STATUS_SEARCH_OPTIONS[6]:
                statuses = [
                    ClaimSearchRequest.StatusListEnum.Active,
                    ClaimSearchRequest.StatusListEnum.Denied,
                    ClaimSearchRequest.StatusListEnum.Rejected,
                    ClaimSearchRequest.StatusListEnum.UnderAppeal,
                    ClaimSearchRequest.StatusListEnum.Pending,
                ];
                break;
            case STATUS_SEARCH_OPTIONS[1]:
                statuses = [ClaimSearchRequest.StatusListEnum.Active];
                break;
            case STATUS_SEARCH_OPTIONS[2]:
                statuses = [ClaimSearchRequest.StatusListEnum.Denied];
                break;
            case STATUS_SEARCH_OPTIONS[3]:
                statuses = [ClaimSearchRequest.StatusListEnum.Pending];
                break;
            case STATUS_SEARCH_OPTIONS[4]:
                statuses = [ClaimSearchRequest.StatusListEnum.Rejected];
                break;
            case STATUS_SEARCH_OPTIONS[5]:
                statuses = [ClaimSearchRequest.StatusListEnum.UnderAppeal];
                break;
            default:
                break;
        }
        return statuses;
    }

    getStalledStatusForSearch() {
        if (this.claimStatusFilter.value === STATUS_SEARCH_OPTIONS[6]) {
            return false;
        } else if (
            this.claimStalledFilterService.getFilterStatusValue(
                STALLED_CLAIM_FILTER_IDS.BY_STATUS,
            )
        ) {
            return null;
        } else {
            return true;
        }
    }

    getPagedClaimsBySearch() {
        let claimSearch: PagedClaimSearchRequest = {
            statusList: this.getStatusForSearch(),
            accountPayorId: this.selectedPayer.value.accountPayor?.id,
            notSnoozed:
                this.searchService.getSnoozedSearchOptionFromSelectedValue(
                    this.getSelectedSnoozeValue(),
                ),
            uploadAccountLocationIdIdList:
                this.authService.isAgingUploadByLocationOff(
                    this.authService.getAgingLocationFilter(),
                )
                    ? undefined
                    : this.authService.getAgingLocationFilter(),
            paged: this.getPaging(),
            stalledReasonIsNull: this.getStalledStatusForSearch(),
        };
        if (!claimSearch.accountPayorId) {
            claimSearch.accountPayorIsNull = true;
        }
        this.searchService
            .searchClaimsPaged(claimSearch)
            .subscribe((claims) => {
                this.PAGED_CLAIMS.next(claims);
            });
        this.getSnoozeCountForSearch(claimSearch);
    }

    getSnoozeCountForSearch(claimSearch: ClaimSearchRequest) {
        let snoozeSearch: ClaimSearchRequest = JSON.parse(
            JSON.stringify(claimSearch),
        );
        snoozeSearch.notSnoozed =
            this.searchService.getSnoozedSearchOptionFromSelectedValue(
                SnoozeFilterOption.Snoozed,
            );
        this.searchService
            .searchClaimsPaged(snoozeSearch)
            .subscribe((claims) => {
                this.snoozedClaimCount.next(claims.totalCount);
            });
    }

    getPaging(): SortablePagedRequestClaimSortBy {
        const tableId = this.tableService.generateTableId();

        const direction = this.searchService.mapSortDirectionResponseToRequest(
            this.tableService.getTableState(tableId)?.sort?.direction,
        );
        this.paging.sortDirection = direction
            ? direction
            : this.paging.sortDirection;

        const field = this.searchService.mapSortNameResponseToRequest(
            this.tableService.getTableState(tableId)?.sort?.active,
        );
        this.paging.sortFieldName = field ? field : this.paging.sortFieldName;

        // Make sure our saved sort matches current actual sort
        const fieldNameToSave = this.searchService.mapSortNameRequestToResponse(
            this.paging.sortFieldName,
        );
        const directionToSave =
            this.searchService.mapSortDirectionRequestToResponse(
                this.paging.sortDirection,
            );
        this.tableService.updateSort(tableId, fieldNameToSave, directionToSave);

        return {
            pageNumber: this.paging.pageNumber,
            unPaged: this.paging.unPaged,
            /* Use global page size */
            pageSize: this.searchService.getClaimPageSize(),
            sortRequests: [
                {
                    sortFieldName:
                        SortablePagedRequestClaimSortBy.SortFieldNameEnum
                            .Stalled,
                    sortDirection:
                        SortablePagedRequestClaimSortBy.SortDirectionEnum.Asc,
                },
                {
                    sortFieldName: this.paging.sortFieldName,
                    sortDirection: this.paging.sortDirection,
                },
            ],
        };
    }

    changePage(event: PageEvent) {
        this.searchService.setClaimPageSize(event.pageSize);
        this.paging.pageNumber = event.pageIndex + 1;
        this.getPagedClaimsBySearch();
    }

    changeSort() {
        this.getPagedClaimsBySearch();
    }

    clearPayorSnoozeFilters() {
        this.setSelectedPayerValue(undefined);
        this.setSelectedSnoozeValue(SnoozeFilterOption.All);
    }
}
